import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Header } from "../stories/es/Header/Header";
import { Font } from "../stories/es/Fonts/Font";
import {
  getAdminRoleFromAuth,
  hasAdminAccess,
  isAdminUser,
  getDefaultAdminRoute,
  ROUTE_TO_SECTION,
} from "./adminRoles";

/**
 * Wrapper component for admin pages that checks role-based access.
 *
 * Usage as a direct wrapper:
 *   <AdminProtectedPage section="dashboard">
 *     <YourAdminPageContent />
 *   </AdminProtectedPage>
 *
 * Usage as a layout route (renders <Outlet />):
 *   <Route element={<AdminProtectedPage />}>
 *     <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
 *   </Route>
 *
 * When no `section` prop is provided, the section is inferred from the
 * current route path via ROUTE_TO_SECTION.
 *
 * If the user is an admin but lacks access to this section, they are
 * redirected to their default allowed page.
 * If the user is not an admin at all, a 403 message is displayed.
 */
const AdminProtectedPage = ({ section, children }) => {
  const authUser = useSelector((state) => state?.authentication?.user);
  const location = useLocation();
  const isAdmin = isAdminUser(authUser);
  const adminRole = getAdminRoleFromAuth(authUser);

  // Resolve section: use prop if given, otherwise look up from current path
  const resolvedSection = section || ROUTE_TO_SECTION[location.pathname] || "dashboard";
  const hasAccess = isAdmin && hasAdminAccess(adminRole, resolvedSection);

  if (!isAdmin) {
    return (
      <div>
        <Font />
        <Header />
        <div
          style={{
            maxWidth: 600,
            margin: "80px auto",
            textAlign: "center",
            padding: "40px 20px",
          }}
        >
          <h1 style={{ color: "#e74c3c", fontSize: 48, marginBottom: 16 }}>
            403
          </h1>
          <h2 style={{ marginBottom: 12 }}>Access Denied</h2>
          <p style={{ color: "#666" }}>
            You do not have admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    // Redirect unauthorized admin roles to their default allowed page
    const defaultRoute = getDefaultAdminRoute(adminRole);
    return <Navigate to={defaultRoute} replace />;
  }

  // Support both children (direct wrapper) and Outlet (layout route)
  return children ? <>{children}</> : <Outlet />;
};

export default AdminProtectedPage;
